import React, { useEffect, useState } from 'react';
import { FiHash } from 'react-icons/fi';

interface InsuredTag {
  databaseId?: string | null;
  tagDatabaseId?: string | null;
  name?: string | null;
  description?: string | null;
  categoryName?: string | null;
  color?: string | null;
}

// NowCerts tag colors arrive as hex ("#ff0000" / "ff0000"); anything else falls
// back to the panel's neutral purple chip.
const toHexColor = (raw: string | null | undefined): string | null => {
  const v = (raw || '').trim().replace(/^#/, '');
  return /^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) ? `#${v}` : null;
};

/**
 * NowCerts tags applied to an insured (Insured panel) or to a policy (Policy
 * panel, pass `policyId`), shown as colored chips. Non-critical: renders
 * nothing while loading, on error, or when there are no tags.
 */
const InsuredTags: React.FC<{ insuredId?: string | null; policyId?: string | null }> = ({ insuredId, policyId }) => {
  const [tags, setTags] = useState<InsuredTag[]>([]);

  useEffect(() => {
    setTags([]);
    const path = policyId
      ? `policy/${encodeURIComponent(policyId)}/tags`
      : insuredId ? `insured/${encodeURIComponent(insuredId)}/tags` : null;
    if (!path) return;
    let cancelled = false;
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url)}/api/ampnowcerts/${path}`,
      { headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken } }
    )
      .then(async (res) => {
        if (!res.ok) return;
        const json = await res.json();
        const items: InsuredTag[] = Array.isArray(json) ? json : (json?.tags ?? []);
        if (!cancelled) setTags(items.filter((t) => (t.name || '').trim()));
      })
      .catch(() => { /* tags are optional — fail silently */ });
    return () => { cancelled = true; };
  }, [insuredId, policyId]);

  if (tags.length === 0) return null;

  return (
    <div className="isp__tags">
      <span className="isp__tags-label"><FiHash size={11} /> Tags</span>
      <div className="isp__tags-list">
        {tags.map((t, i) => {
          const color = toHexColor(t.color);
          const title = [t.name, t.categoryName, t.description].map((v) => (v || '').trim()).filter(Boolean).join(' · ');
          return (
            <span
              key={t.databaseId || t.tagDatabaseId || `${t.name}-${i}`}
              className="isp__tag"
              title={title}
              style={color ? { color, borderColor: `${color}66`, background: `${color}1a` } : undefined}
            >
              {color && <span className="isp__tag-dot" style={{ background: color }} />}
              {t.name}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default InsuredTags;
